import React, { useState, useEffect } from 'react';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { useNavigate } from 'react-router-dom';
import { API_CONFIG } from '../utils/constants';

export default function CheckoutEmbeddedPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);

  // Initialize Stripe with key from backend
  useEffect(() => {
    const initStripe = async () => {
      try {
        const response = await fetch(`${API_CONFIG.BASE_URL}/api/stripe/config`);
        const data = await response.json();
        
        if (data.success && data.publishable_key) {
          const key = data.publishable_key;
          setStripePromise(loadStripe(key));
        } else {
          throw new Error('Failed to get Stripe config');
        }
      } catch (err) {
        console.error('❌ [CheckoutEmbeddedPage] Failed to initialize Stripe:', err);
        setError('Failed to initialize payment system');
      }
    };
    
    initStripe();
  }, []);

  const fetchClientSecret = async () => {
    try {
      setError(null);
      
      // Placeholder endpoint - will be implemented server-side later
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: `HTTP ${response.status}: ${response.statusText}`,
        }));
        throw new Error(errorData.message || 'Failed to create checkout session');
      }

      const data = await response.json();
      
      if (!data.clientSecret) {
        throw new Error('Invalid response: clientSecret is missing');
      }

      return data.clientSecret;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to initialize checkout';
      setError(errorMessage);
      throw err;
    }
  };

  return (
    <div className="min-h-full flex flex-col items-center justify-center py-12">
      <div className="fluid-container w-full">
        <div className="mb-6">
          <button
            onClick={() => navigate('/')}
            className="text-white/70 hover:text-white transition-colors text-sm mb-4"
          >
            ← Back to home
          </button>
          <h1 className="text-3xl font-bold text-white mb-2">Checkout</h1>
          <p className="text-white/70">Complete your purchase</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400">
            {error}
          </div>
        )}

        <div className="bg-bg border border-white/20 rounded-xl p-6">
          {stripePromise ? (
            <EmbeddedCheckoutProvider
              stripe={stripePromise}
              options={{ fetchClientSecret }}
            >
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          ) : (
            <div className="text-white/70 text-center py-8">
              Initializing payment system...
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

